import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ApiError, api, type Me, type Schemas, unwrap } from "../../api/client";
import { keys, useMe, useSignOut } from "../../api/hooks";
import { DevicesCard } from "../../components/me/DevicesCard";
import { ProfileCard } from "../../components/me/ProfileCard";
import { PasskeysCard } from "../../components/passkeys/PasskeysCard";
import { AppVersion } from "../../components/shell/AppVersion";
import { PageHead } from "../../components/shell/Page";
import { FieldReadout } from "../../components/ui/FieldReadout";
import { Choice, Toggle } from "../../components/ui/primitives";
import { setLang, t } from "../../i18n";
import {
  DEFAULT_QUIET,
  digestOptions,
  digestPayload,
  digestValue,
  quietOf,
  quietPayload,
  zoneLabel,
  zoneOptions,
  zonePayload,
} from "../../lib/prefs";
import {
  browserEnv,
  currentSubscription,
  PUSH_REASON,
  pushSupport,
  sameKey,
  subscribeBody,
  subscribeDevice,
} from "../../lib/push";
import { useUi } from "../../state/ui";

export const Route = createFileRoute("/_app/me")({ component: Preferences });

type Prefs = Schemas["PreferencesIn"];

function usePrefs() {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  return useMutation({
    mutationFn: (body: Prefs) => unwrap(api.PATCH("/api/me/preferences", { body })),
    onSuccess: (data) => {
      qc.setQueryData(keys.me, data);
      // A new interface language: the shell mounts again in it; the toast says it already.
      setLang(data.ui_language);
      toast(t("me.saved"));
    },
    onError: (e) => toast(e.message, true),
  });
}

function Preferences() {
  const me = useMe();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const save = usePrefs();
  if (!me.data) return null;
  return (
    <main className="sub main on no-panel" aria-label={t("me.preferences")}>
      <div className="page">
        <PageHead title={t("me.preferences")} subtitle={me.data.email} />
        <div className="team-editor scroll">
          <div style={{ maxWidth: 640 }}>
            <ProfileCard me={me.data} />
            <section className="card-sec">
              <div className="cs-head">
                <b>{t("me.howVantageTalks")}</b>
              </div>
              <div className="cs-body frm">
                <div className="fld">
                  <span className="lbl">{t("me.language")}</span>
                  <Choice
                    label={t("me.language")}
                    value={me.data.language}
                    options={[
                      { value: "auto", label: t("me.matchMyMessage") },
                      { value: "vi", label: "Tiếng Việt" },
                      { value: "en", label: "English" },
                    ]}
                    onChange={(language) => save.mutate({ language })}
                  />
                </div>
                <div className="fld">
                  <span className="lbl">{t("me.interfaceLanguage")}</span>
                  <Choice
                    label={t("me.interfaceLanguage")}
                    value={me.data.ui_language}
                    options={[
                      // Each language named in itself, in both languages: a person finds their own.
                      { value: "en", label: "English" },
                      { value: "vi", label: "Tiếng Việt" },
                    ]}
                    onChange={(ui_language) => save.mutate({ ui_language })}
                  />
                  <div className="help">{t("me.interfaceLanguageHelp")}</div>
                </div>
                <div className="fld">
                  <span className="lbl">{t("me.reachBy")}</span>
                  <Choice
                    label={t("me.reachBy")}
                    value={me.data.reach_by}
                    options={[
                      { value: "web", label: t("me.reachWeb") },
                      { value: "email", label: t("me.reachEmail") },
                      { value: "both", label: t("me.reachBoth") },
                    ]}
                    onChange={(reach_by) => save.mutate({ reach_by })}
                  />
                  <div className="help">{t("me.reachByHelp")}</div>
                </div>
              </div>
            </section>
            <NotificationPrefs me={me.data} save={(body) => save.mutate(body)} />
            <PushCard me={me.data} />
            <PasskeysCard />
            <DevicesCard />
            <div className="te-foot">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  // Past any unsaved-changes question (R9-A7): the session has ended.
                  signOut.mutate(undefined, {
                    onSettled: () => navigate({ to: "/signin", ignoreBlocker: true }),
                  })
                }
              >
                {t("me.signOut")}
              </button>
            </div>
            <AppVersion />
          </div>
        </div>
      </div>
    </main>
  );
}

/** Quiet hours, the email digest and the time zone they're read in (PLAN §8.7). Each saves
 *  as it changes, like the rest of this page; a time saves once it's a whole, valid time. */
function NotificationPrefs({ me, save }: { me: Me; save: (body: Prefs) => void }) {
  const saved = quietOf(me.quiet_hours);
  const [start, setStart] = useState(saved?.start ?? DEFAULT_QUIET.start);
  const [end, setEnd] = useState(saved?.end ?? DEFAULT_QUIET.end);
  // A change saved elsewhere (another device) shows here.
  useEffect(() => {
    if (!saved) return;
    setStart(saved.start);
    setEnd(saved.end);
  }, [saved?.start, saved?.end]);
  // What was last sent, so typing then leaving the field doesn't save twice.
  const sent = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const saveTimes = (s: string, e: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (!saved) return;
    const body = quietPayload(true, s, e);
    if (!body?.quiet_hours) return;
    const key = `${body.quiet_hours.start}-${body.quiet_hours.end}`;
    if (key === `${saved.start}-${saved.end}` || key === sent.current) return;
    sent.current = key;
    save(body);
  };
  const later = (s: string, e: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => saveTimes(s, e), 700);
  };
  const device =
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;
  const zones = zoneOptions(me.timezone, device);
  return (
    <section className="card-sec" data-testid="notification-prefs">
      <div className="cs-head">
        <b>{t("me.notifications")}</b>
        <span className="cs-sub">{t("me.notificationsSub")}</span>
      </div>
      <div className="perm-row">
        <div className="grow">
          <div className="sname">{t("me.quietHours")}</div>
          <div className="smeta">{t("me.quietHoursHelp")}</div>
        </div>
        <Toggle
          checked={Boolean(saved)}
          label={t("me.quietHours")}
          onChange={(on) => {
            sent.current = null;
            const body = quietPayload(on, start, end);
            if (body) save(body);
          }}
        />
      </div>
      {saved && (
        <div className="cs-body frm" data-testid="quiet-hours">
          <div className="row2">
            <div className="fld">
              <label htmlFor="quiet-start">{t("me.quietFrom")}</label>
              <input
                id="quiet-start"
                className="inp"
                type="time"
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                  later(e.target.value, end);
                }}
                onBlur={() => saveTimes(start, end)}
              />
              <FieldReadout value={start} kind="time" />
            </div>
            <div className="fld">
              <label htmlFor="quiet-end">{t("me.quietTo")}</label>
              <input
                id="quiet-end"
                className="inp"
                type="time"
                value={end}
                onChange={(e) => {
                  setEnd(e.target.value);
                  later(start, e.target.value);
                }}
                onBlur={() => saveTimes(start, end)}
              />
              <FieldReadout value={end} kind="time" />
            </div>
          </div>
          {start.slice(0, 5) === end.slice(0, 5) && (
            <div className="form-error">{t("me.quietSameTime")}</div>
          )}
        </div>
      )}
      <div className="src-row">
        <div className="grow">
          <div className="sname">
            <label htmlFor="email-digest">{t("me.digestLabel")}</label>
          </div>
          <div className="smeta">
            {me.email_enabled ? t("me.digestHelp") : t("me.digestHelpOff")}
          </div>
        </div>
        <select
          id="email-digest"
          className="inp select"
          data-testid="email-digest"
          value={digestValue(me.email_fallback_minutes)}
          onChange={(e) => save(digestPayload(e.target.value))}
        >
          {digestOptions(me.email_fallback_minutes).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="src-row">
        <div className="grow">
          <div className="sname">
            <label htmlFor="time-zone">{t("me.timeZone")}</label>
          </div>
          <div className="smeta">{t("me.timeZoneHelp")}</div>
        </div>
        <select
          id="time-zone"
          className="inp select"
          data-testid="time-zone"
          value={me.timezone ?? ""}
          onChange={(e) => save(zonePayload(e.target.value))}
        >
          <option value="">{t("me.companyTimeZone")}</option>
          {zones.map((z) => (
            <option key={z} value={z}>
              {zoneLabel(z)}
              {z === device ? t("me.zoneThisDevice") : ""}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}

/** "Get notifications on this device" (PLAN §8.7 browser push): the switch registers the
 *  service worker, asks the browser's permission and sends the subscription to the server;
 *  off removes both. Where push can't work, a line says why instead. */
function PushCard({ me }: { me: Me }) {
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const key = useQuery({
    queryKey: ["push", "key"],
    queryFn: () => unwrap(api.GET("/api/push/key")),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const support = key.isLoading ? null : pushSupport(browserEnv(), key.data?.public_key);
  // On for this device: this browser holds a subscription made with the server's current key,
  // and the server has one for this person (a browser someone else used, or a rotated key,
  // shows Off; turning it on takes the subscription over).
  const [here, setHere] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const publicKey = key.data?.public_key ?? null;
  useEffect(() => {
    if (!support?.ok || !publicKey) return;
    void currentSubscription()
      .then((s) =>
        setHere(
          Boolean(s) && sameKey(s?.options.applicationServerKey, publicKey) && me.push_subscribed,
        ),
      )
      .catch(() => setHere(false));
  }, [support?.ok, publicKey, me.push_subscribed]);
  const refreshMe = () => qc.invalidateQueries({ queryKey: keys.me });

  const turnOn = async () => {
    if (!publicKey) return;
    setBusy(true);
    try {
      const post = async (fresh: boolean) => {
        const sub = await subscribeDevice(publicKey, fresh);
        const body = subscribeBody(sub.toJSON());
        if (!body) throw new Error(t("me.pushIncomplete"));
        await unwrap(api.POST("/api/push/subscribe", { body }));
      };
      try {
        await post(false);
      } catch (e) {
        // This browser still holds someone else's subscription (their session ended without
        // signing out): make a new one of our own (review round 2).
        if (!(e instanceof ApiError && e.code === "taken")) throw e;
        await post(true);
      }
      setHere(true);
      toast(t("me.pushOnToast"));
      void refreshMe();
    } catch (e) {
      toast(e instanceof Error && e.message ? e.message : t("me.pushOnFailed"), true);
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        await unwrap(api.DELETE("/api/push/subscribe", { body: { endpoint: sub.endpoint } }));
        await sub.unsubscribe().catch(() => false);
      }
      setHere(false);
      toast(t("me.pushOffToast"));
      void refreshMe();
    } catch {
      toast(t("me.pushOffFailed"), true);
    } finally {
      setBusy(false);
    }
  };

  let line: string;
  if (support === null) line = t("me.pushChecking");
  else if (!support.ok) line = PUSH_REASON[support.reason];
  else if (here) line = t("me.pushOnHere");
  else if (me.push_subscribed) line = t("me.pushOnElsewhere");
  else line = t("me.pushOffHere");

  return (
    <section className="card-sec" data-testid="push-card">
      <div className="cs-head">
        <b>{t("me.pushTitle")}</b>
        <span className="cs-sub">{t("me.thisDevice")}</span>
      </div>
      <div className="perm-row" data-testid="push-state" data-state={here ? "on" : "off"}>
        <div className="grow">
          <div className="sname">{t("me.pushRow")}</div>
          <div className="smeta">{line}</div>
        </div>
        {support?.ok && here !== null && (
          <Toggle
            checked={here}
            label={t("me.pushTitle")}
            disabled={busy}
            onChange={(on) => void (on ? turnOn() : turnOff())}
          />
        )}
      </div>
    </section>
  );
}
